import React, { Fragment, useState, useRef, useMemo, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Paper, Box, Typography } from "@mui/material";
import { LangConstant } from "const";
import { useUserList, useConferenceList, useUpdateRoleAccount, useUpdateConference } from "hooks";
import { PageTitle } from "components";
import { ActionDialog, Datatable, Heading, EditAppDialog, ConferenceDetail } from "./components";
import { LodashUtils } from "utils";
import AppConfig from "./components/AppConfig";

const VIEW_MODE = {
  list: "list",
  detail: "detail",
  config: "config",
};

const ConferenceManagement = () => {
  const { t: getLabel } = useTranslation();
  const titleObjectContent = getLabel(LangConstant.OBJ_SIDEBAR_TITLE, { returnObjects: true });
  const checkedUsersRef = useRef([]);

  const { pagination, conferences, handleChangePage, refetchConference } = useConferenceList();
  const { updateRoleAccount } = useUpdateRoleAccount();
  const { updateConference } = useUpdateConference();
  const {
    form,
    setForm,
    filterUsers,
    currentPageUsers,
    pagination: userPagination,
    handleChangeSearchInput: handleUserSearch,
    handleChangePageWithStatusActive,
    handleChangeUnitDropdown,
    handleChangeDepartmentDropdown,
  } = useUserList();

  const [searchQuery, setSearchQuery] = useState("");
  const [actionDialogConfig, setActionDialogConfig] = useState({ open: false, conference: null, isEdit: false });
  const [editAppDialogConfig, setEditAppDialogConfig] = useState({ open: false, conference: null });
  const [selectedApp, setSelectedApp] = useState(null);
  const [viewMode, setViewMode] = useState(VIEW_MODE.list);

  const filteredConferences = useMemo(() => {
    return conferences.filter(conference => conference.name.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [conferences, searchQuery]);

  const handleChangeViewMode = viewMode => selectedApp => {
    setSelectedApp(selectedApp);
    setViewMode(viewMode);
  };

  const handleOpenEditAdminDialog = useCallback(
    conference => {
      setForm(prev => ({ ...prev, state: 1 }));
      const adminUser = filterUsers.filter(user => conference?.adminAccountIds?.includes(user.accountId));
      checkedUsersRef.current = adminUser;
      setActionDialogConfig({ open: true, conference, isEdit: true });
    },
    [setForm, filterUsers],
  );

  const handleCloseActionDialog = () => {
    setForm(prev => ({ ...prev, search: "" }));
    setActionDialogConfig({ open: false, conference: null, isEdit: false });
    refetchConference();
  };

  const handleCloseEditAppDialog = () => {
    setEditAppDialogConfig({ open: false, conference: null });
  };

  const handleUpdateAccountRole = form => {
    updateRoleAccount({
      serviceId: form.serviceId,
      accountIds: form.accountIds,
      onClose: handleCloseActionDialog,
    });
  };

  const handleUpdateConference = form => {
    updateConference({
      conferenceId: form.conferenceId,
      title: form.title,
      description: form.description,
      onClose: handleCloseActionDialog,
    });
  };

  const handleBackToList = () => {
    setSelectedApp(null);
    setViewMode(VIEW_MODE.list);
  };

  useEffect(() => {
    if (selectedApp) {
      const updated = filteredConferences.find(conf => conf.serviceId === selectedApp.serviceId);
      if (updated) {
        if (!LodashUtils.isEqual(updated, selectedApp)) {
          setSelectedApp(updated);
        }
      }
    }
  }, [filteredConferences]);

  return (
    <Fragment>
      <PageTitle>{getLabel(titleObjectContent.conferenceManagement)}</PageTitle>

      {viewMode === VIEW_MODE.list && (
        <Paper sx={{ px: 2, py: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography sx={{ fontWeight: "bold", fontSize: "20px", marginBottom: "10px" }}>
              Danh sách ứng dụng
            </Typography>
          </Box>
          <Heading searchInput={searchQuery} onChangeSearchInput={setSearchQuery} />

          <Datatable
            conferences={filteredConferences}
            pagination={pagination}
            onChangePage={handleChangePage}
            onViewDetails={handleChangeViewMode(VIEW_MODE.detail)}
            onViewConfig={handleChangeViewMode(VIEW_MODE.config)}
          />
        </Paper>
      )}

      {viewMode === VIEW_MODE.detail && (
        <ConferenceDetail
          conference={selectedApp}
          onBack={handleBackToList}
          handleOpenEditAdminDialog={handleOpenEditAdminDialog}
          form={form}
          setForm={setForm}
          filterUsers={filterUsers}
          handleChangeSearchInput={handleUserSearch}
          handleChangeUnitDropdown={handleChangeUnitDropdown}
          handleChangeDepartmentDropdown={handleChangeDepartmentDropdown}
          handleUpdateAccountRole={handleUpdateAccountRole}
        />
      )}

      {viewMode === VIEW_MODE.config && (
        <AppConfig appName={selectedApp.name} defaultConfig={selectedApp.config} onBack={handleBackToList} />
      )}

      <ActionDialog
        form={form}
        open={actionDialogConfig.open}
        conference={selectedApp}
        onClose={handleCloseActionDialog}
        onChangeSearchInput={handleUserSearch}
        isEdit={actionDialogConfig.isEdit}
        checkedUsers={checkedUsersRef.current}
        handleUpdateAccountRole={handleUpdateAccountRole}
        pagination={userPagination}
        currentPageUsers={currentPageUsers}
        onChangePage={handleChangePageWithStatusActive}
      />
      <EditAppDialog
        open={editAppDialogConfig.open}
        conference={editAppDialogConfig.conference}
        onClose={handleCloseEditAppDialog}
        onUpdate={handleUpdateConference}
      />
    </Fragment>
  );
};

export default ConferenceManagement;
